import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export const exportMaterialTransportSummary = async (
  transportData = [],
  fileName = "Material_Transport_Summary",
) => {
  if (!Array.isArray(transportData) || transportData.length === 0) {
    alert("No data available to export");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Summary");

  // --------------------------------------------------
  // 1. Group by Field Technician
  // --------------------------------------------------

  const technicianMap = new Map();

  transportData.forEach((item) => {
    const technicianId = item.ft_id ?? item.fieldTechnician?.id ?? "unknown";

    const technicianName = item.fieldTechnician?.fullName?.trim() || "Unknown";

    const supervisorName = item.supervisor?.fullName?.trim() || "";

    if (!technicianMap.has(technicianId)) {
      technicianMap.set(technicianId, {
        technician: technicianName,
        supervisor: supervisorName,
        materials: new Map(),
      });
    }

    const technician = technicianMap.get(technicianId);

    // --------------------------------------------------
    // Group by Material
    // --------------------------------------------------

    const materialId = item.material_id ?? item.material?.id ?? "unknown";

    const materialName = item.material?.name?.trim() || "Unknown";

    if (!technician.materials.has(materialId)) {
      technician.materials.set(materialId, {
        materialId,
        materialName,

        quantityDispatchedFromFactory: 0,
        quantityReceivedAtSite: 0,
        quantityDispatchedFromSite: 0,
        quantityReceivedAtFactory: 0,
        excessAtFactory: 0,
        excessAtSite: 0,
      });
    }

    const material = technician.materials.get(materialId);

    // --------------------------------------------------
    // Add quantities
    // --------------------------------------------------

    material.quantityDispatchedFromFactory +=
      Number(item.quantityDispatchedFromFactory) || 0;

    material.quantityReceivedAtSite += Number(item.quantityReceivedAtSite) || 0;

    material.quantityDispatchedFromSite +=
      Number(item.quantityDispatchedFromSite) || 0;

    material.quantityReceivedAtFactory +=
      Number(item.quantityReceivedAtFactory) || 0;

    material.excessAtFactory += Number(item.excessAtFactory) || 0;

    material.excessAtSite += Number(item.excessAtSite) || 0;
  });

  // --------------------------------------------------
  // 2. Sort technicians alphabetically
  // --------------------------------------------------

  const technicians = [...technicianMap.values()].sort((a, b) =>
    a.technician.localeCompare(b.technician, undefined, {
      sensitivity: "base",
    }),
  );

  // Get all unique materials from transport data
  const materialsMap = new Map();

  transportData.forEach((item) => {
    const materialId = item.material_id ?? item.material?.id ?? "unknown";
    const materialName = item.material?.name?.trim() || "Unknown";

    if (!materialsMap.has(materialId)) {
      materialsMap.set(materialId, {
        id: materialId,
        name: materialName,
      });
    }
  });

  const materials = [...materialsMap.values()].sort((a, b) =>
    b.name.localeCompare(a.name, undefined, {
      sensitivity: "base",
    }),
  );

  // --------------------------------------------------
  // 3. Create columns
  // --------------------------------------------------

  worksheet.columns = [
    {
      header: "SL NO",
      key: "slNo",
      width: 10,
    },
    {
      header: "MATERIAL",
      key: "material",
      width: 22,
    },
    {
      header: "QTY DISPATCHED\nFROM FACTORY",
      key: "quantityDispatchedFromFactory",
      width: 24,
    },
    {
      header: "QTY RECEIVED\nAT SITE",
      key: "quantityReceivedAtSite",
      width: 20,
    },
    {
      header: "QTY DISPATCHED\nFROM SITE",
      key: "quantityDispatchedFromSite",
      width: 22,
    },
    {
      header: "QTY RECEIVED\nAT FACTORY",
      key: "quantityReceivedAtFactory",
      width: 22,
    },
    {
      header: "EXCESS QTY\nAT FACTORY",
      key: "excessAtFactory",
      width: 20,
    },
    {
      header: "EXCESS QTY\nAT SITE",
      key: "excessAtSite",
      width: 20,
    },
    {
      header: "FIELD TECHNICIAN",
      key: "technician",
      width: 28,
    },
    {
      header: "SUPERVISOR",
      key: "supervisor",
      width: 25,
    },
  ];

  // --------------------------------------------------
  // 4. Header styling
  // --------------------------------------------------

  const headerRow = worksheet.getRow(1);

  headerRow.height = 55;

  headerRow.eachCell((cell) => {
    cell.font = {
      bold: true,
      size: 10,
    };

    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };

    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // --------------------------------------------------
  // 5. Add technician blocks
  // --------------------------------------------------

  // let serialNumber = 1;

  // technicians.forEach((technician) => {
  //   const startRow = worksheet.rowCount + 1;

  //   // Use ALL materials for every technician
  //   materials.forEach((material) => {
  //     // Check whether this technician has this material
  //     const technicianMaterial = technician.materials.get(material.id);

  //     worksheet.addRow({
  //       slNo: "",
  //       material: material.name,

  //       quantityDispatchedFromFactory:
  //         technicianMaterial?.quantityDispatchedFromFactory ?? 0,

  //       quantityReceivedAtSite: technicianMaterial?.quantityReceivedAtSite ?? 0,

  //       quantityDispatchedFromSite:
  //         technicianMaterial?.quantityDispatchedFromSite ?? 0,

  //       quantityReceivedAtFactory:
  //         technicianMaterial?.quantityReceivedAtFactory ?? 0,

  //       excessAtFactory: technicianMaterial?.excessAtFactory ?? 0,

  //       excessAtSite: technicianMaterial?.excessAtSite ?? 0,

  //       technician: "",
  //       supervisor: "",
  //     });
  //   });

  //   const endRow = worksheet.rowCount;

  //   // Put technician information in first row
  //   worksheet.getCell(startRow, 1).value = serialNumber;

  //   worksheet.getCell(startRow, 9).value = technician.technician;

  //   worksheet.getCell(startRow, 10).value = technician.supervisor;

  //   // --------------------------------------------------
  //   // Merge SL NO
  //   // --------------------------------------------------

  //   if (startRow !== endRow) {
  //     worksheet.mergeCells(startRow, 1, endRow, 1);

  //     worksheet.mergeCells(startRow, 9, endRow, 9);

  //     worksheet.mergeCells(startRow, 10, endRow, 10);
  //   }

  //   serialNumber++;
  // });

  // --------------------------------------------------
  // 5. Add technician blocks
  // --------------------------------------------------

  let serialNumber = 1;

  technicians.forEach((technician) => {
    const startRow = worksheet.rowCount + 1;

    // Use ALL materials for every technician
    materials.forEach((material, index) => {
      // Check whether this technician has this material
      const technicianMaterial = technician.materials.get(material.id);
      const daVal =
        technicianMaterial?.quantityReceivedAtFactory -
        technicianMaterial?.quantityDispatchedFromFactory;
      const finalExcessAtFactory = daVal <= 0 || isNaN(daVal) ? 0 : daVal;
      const bcVal =
        technicianMaterial?.quantityReceivedAtSite -
        technicianMaterial?.quantityDispatchedFromSite;
      const finalExcessAtSite = bcVal <= 0 || isNaN(bcVal) ? 0 : bcVal;

      worksheet.addRow({
        // Show SL NO only on first material row
        slNo: index === 0 ? serialNumber : "",

        material: material.name,

        quantityDispatchedFromFactory:
          technicianMaterial?.quantityDispatchedFromFactory ?? 0,

        quantityReceivedAtSite: technicianMaterial?.quantityReceivedAtSite ?? 0,

        quantityDispatchedFromSite:
          technicianMaterial?.quantityDispatchedFromSite ?? 0,

        quantityReceivedAtFactory:
          technicianMaterial?.quantityReceivedAtFactory ?? 0,

        excessAtFactory: finalExcessAtFactory,

        excessAtSite: finalExcessAtSite,

        // Show technician in EVERY row
        technician: technician.technician,

        // Show supervisor in EVERY row
        supervisor: technician.supervisor,
      });
    });

    const endRow = worksheet.rowCount;

    // --------------------------------------------------
    // Merge ONLY SL NO
    // --------------------------------------------------

    if (startRow !== endRow) {
      worksheet.mergeCells(startRow, 1, endRow, 1);
    }

    serialNumber++;
  });
  // --------------------------------------------------
  // 6. Body styling
  // --------------------------------------------------

  for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
    const row = worksheet.getRow(rowNumber);

    row.height = 32;

    row.eachCell((cell) => {
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };

      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
  }

  // --------------------------------------------------
  // 7. Make merged technician information bold
  // --------------------------------------------------

  for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
    worksheet.getCell(rowNumber, 1).font = {
      bold: true,
    };

    worksheet.getCell(rowNumber, 9).font = {
      bold: true,
    };

    worksheet.getCell(rowNumber, 10).font = {
      bold: true,
    };
  }

  // --------------------------------------------------
  // 8. Freeze header
  // --------------------------------------------------

  worksheet.views = [
    {
      state: "frozen",
      ySplit: 1,
    },
  ];

  // --------------------------------------------------
  // 9. Generate Excel
  // --------------------------------------------------

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${fileName}.xlsx`,
  );
};
